import React, { lazy } from 'react'
import { Navigate, Routes, Route } from 'react-router-dom'
import useInstallationsContext from '../Libs/installations/hooks/use-installations-context'
import { useAppRoutes } from '../Libs/navigation/hooks'
import { UserData } from '../State/User/UserModel'
import { ADMIN_ROLES, SERVICE_LEVELS, SHOP_ACCESS } from '../Configuration/user'
import { RouteContentSkeleton } from '@e3dc-react/shell/Elements/Skeletons'
import RequireUserLevel from 'src/Elements/RequireUserLevel/RequireUserLevel'
import BannerProvider from 'src/Libs/banners/provider/banner-provider'
import { ShopProductCategoryProvider } from 'src/Libs/shop/provider/shop-product-category-provider'
import ShopMaintenance from './Shop/Maintenance/ShopMaintenance'
import { isFlowTheme } from 'src/Libs/utils'
import SystemConfigurationEntry from './Shop/SystemConfiguration/SystemConfigurationEntry'
import InstallationRouteGuard from 'src/Libs/navigation/components/InstallationRouteGuard'
import RequireStorageId from 'src/Elements/RequireStorageId/RequireStorageId'
import { useFeatureFlagsContext } from 'src/Libs/feature-flags/FeatureFlagsContext'

const AppEntryPointSwitch = lazy(() => import('./AppEntryPointSwitch'))
const PvModules = lazy(() => import('./Administration/PvModules/PvModules'))

const Administration = lazy(() => import('./Administration/Administration'))
const Documents = lazy(() => import('./Service/Documents/Downloads'))
const ProductFiles = lazy(() => import('./Service/ProductFiles/ProductFiles'))

// Routes (Business)
const ProjectAdminstrationPage = lazy(() => import('./ProjectAdministration/ProjectAdministrationPage'))
const PowerInverterLayoutPage = lazy(() => import('./PowerInverterLayout/PowerInverterLayoutPage'))
const HomePage = lazy(() => import('./Shop/HomePage/HomePage'))
const ShopProductCategory = lazy(() => import('./Shop/ShopProductCategory/ShopProductCategory'))
const ConfirmCheckout = lazy(() => import('./Shop/Checkout/ConfirmCheckout'))
const Product = lazy(() => import('./Shop/Product/Product'))
const FavoriteSystem = lazy(() => import('./Shop/SystemConfiguration/FavoriteSystem'))
const CheckoutSuccess = lazy(() => import('./Shop/Checkout/CheckoutSuccess'))
const OrderHistory = lazy(() => import('./Shop/OrderHistory/OrderHistory'))
const PromotionCodes = lazy(() => import('./Shop/PromotionCodes/PromotionCodes'))
const NewInstallationRoutes = lazy(() => import('./NewInstallation/NewInstallationRoutes'))
const InstallationConfigurationRoutes = lazy(() => import('./NewInstallation/InstallationConfigurationRoutes'))

interface Props {
  user: UserData
}

/**
 * This component is used so the InstallationContext can be used for useAppRoutes
 * @param user the current user of the portal app
 * @returns the Routes that are rendert inside of Framework
 */
const FrameworkRoutes: React.FC<Props> = ({ user }) => {
  const { installationCount, installationsLoading } = useInstallationsContext()
  const { userRoutes } = useAppRoutes({ user, installationCount })
  const {
    featureFlags: { isProductFilesEnabled },
    isFeatureFlagsLoading,
  } = useFeatureFlagsContext()

  return (
    <BannerProvider>
      <ShopProductCategoryProvider>
        <Routes>
          {userRoutes}
          {isFlowTheme && (
            <>
              <Route
                path="/new-installation/*"
                element={
                  <RequireUserLevel userLevel={user?.level ?? 'USER'} allowedRoles={['EXT_SERVICE_PLUS', 'INSTALLER']}>
                    <NewInstallationRoutes />
                  </RequireUserLevel>
                }
              />
              <Route
                path="/installation/:installationId/properties/advanced-settings/*"
                element={
                  <RequireUserLevel userLevel={user?.level ?? 'USER'} allowedRoles={['EXT_SERVICE_PLUS', 'INSTALLER']}>
                    <InstallationRouteGuard>
                      <RequireStorageId allowAccessWithoutStorageId>
                        <NewInstallationRoutes />
                      </RequireStorageId>
                    </InstallationRouteGuard>
                  </RequireUserLevel>
                }
              />
            </>
          )}

          {!isFlowTheme && (
            <Route
              path="/installation/:installationId/configuration/*"
              element={
                <RequireUserLevel userLevel={user?.level ?? 'USER'} allowedRoles={['E3DC_SERVICE', 'INSTALLER']}>
                  <InstallationConfigurationRoutes />
                </RequireUserLevel>
              }
            />
          )}

          <Route
            path="/administration/documents"
            element={
              <RequireUserLevel userLevel={user?.level ?? 'USER'} allowedRoles={ADMIN_ROLES}>
                <Documents />
              </RequireUserLevel>
            }
          />
          <Route
            path="/product-files"
            element={
              <RequireUserLevel
                userLevel={user?.level ?? 'USER'}
                allowedRoles={isProductFilesEnabled ? SERVICE_LEVELS : []}
                loading={isFeatureFlagsLoading}
              >
                <ProductFiles />
              </RequireUserLevel>
            }
          />
          <Route
            path="/administration/solar-modules/:id?"
            element={
              <RequireUserLevel userLevel={user?.level ?? 'USER'} allowedRoles={SERVICE_LEVELS}>
                <PvModules />
              </RequireUserLevel>
            }
          />
          <Route
            path="/administration"
            element={
              <RequireUserLevel userLevel={user?.level ?? 'USER'} allowedRoles={SERVICE_LEVELS}>
                <Administration />
              </RequireUserLevel>
            }
          />
          <Route
            path="/business/projectAdministration"
            element={
              <RequireUserLevel userLevel={user?.level ?? 'USER'} allowedRoles={SERVICE_LEVELS}>
                <ProjectAdminstrationPage />
              </RequireUserLevel>
            }
          />
          <Route
            path="/business/inverterLayout"
            element={
              <RequireUserLevel userLevel={user?.level ?? 'USER'} allowedRoles={SERVICE_LEVELS}>
                <PowerInverterLayoutPage />
              </RequireUserLevel>
            }
          />
          {/*TODO: Use Nested Routes.*/}
          <Route element={<ShopMaintenance />}>
            <Route
              path="/shop/homepage"
              element={
                <RequireUserLevel userLevel={user?.level ?? 'USER'} allowedRoles={SHOP_ACCESS}>
                  <HomePage />
                </RequireUserLevel>
              }
            />
            <Route
              path="/shop/system-configure/:type/:configurationId?"
              element={
                <RequireUserLevel userLevel={user?.level ?? 'USER'} allowedRoles={SHOP_ACCESS}>
                  <SystemConfigurationEntry />
                </RequireUserLevel>
              }
            />
            <Route
              path="/shop/category/:id/:name"
              element={
                <RequireUserLevel userLevel={user?.level ?? 'USER'} allowedRoles={SHOP_ACCESS}>
                  <ShopProductCategory />
                </RequireUserLevel>
              }
            />

            <Route
              path="/shop/checkout-confirm"
              element={
                <RequireUserLevel userLevel={user?.level ?? 'USER'} allowedRoles={SHOP_ACCESS}>
                  <ConfirmCheckout />
                </RequireUserLevel>
              }
            />
            <Route
              path="/shop/product/:id"
              element={
                <RequireUserLevel userLevel={user?.level ?? 'USER'} allowedRoles={SHOP_ACCESS}>
                  <Product />
                </RequireUserLevel>
              }
            />
            <Route
              path="/shop/favorite-system"
              element={
                <RequireUserLevel userLevel={user?.level ?? 'USER'} allowedRoles={SHOP_ACCESS}>
                  <FavoriteSystem />
                </RequireUserLevel>
              }
            />
            <Route
              path="/shop/order-history"
              element={
                <RequireUserLevel userLevel={user?.level ?? 'USER'} allowedRoles={SHOP_ACCESS}>
                  <OrderHistory />
                </RequireUserLevel>
              }
            />
            <Route
              path="/shop/order-success"
              element={
                <RequireUserLevel userLevel={user?.level ?? 'USER'} allowedRoles={SHOP_ACCESS}>
                  <CheckoutSuccess />
                </RequireUserLevel>
              }
            />
            <Route
              path="/shop/promotion-codes"
              element={
                <RequireUserLevel userLevel={user?.level ?? 'USER'} allowedRoles={SHOP_ACCESS}>
                  <PromotionCodes />
                </RequireUserLevel>
              }
            />
          </Route>
          <Route path="/" element={installationsLoading ? <RouteContentSkeleton /> : <AppEntryPointSwitch />} />
          <Route path="*" element={<Navigate to="/not-found" />} />
        </Routes>
      </ShopProductCategoryProvider>
    </BannerProvider>
  )
}

export default FrameworkRoutes
